/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Button, Container, createStyles, Divider, List, Paper, TablePagination, Theme, Typography } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import { PDFViewer } from '@react-pdf/renderer';
import React, { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { AppState, loadEvaluations, syncSystem } from "../../redux";
import { selectEvals, selectUserId } from "../hooks";
import EvalListItem from "./EvalListItem";
import { ErrorBlock } from './form/components/ErrorBlock';
import LoadingIndicator from './form/components/LoadingIndicator';
import NewEvalButton from "./NewEvalButton";
import Search from "./SearchV2";
import Dropdown from "./Dropdown";
import { evalTypeOptions } from "../../utils/const";
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      zIndex: 0,
      // [theme.breakpoints.up('xs')]: {
      //   overflowY: "scroll",
      // },
      WebkitOverflowScrolling: "touch",
    },
    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      position: "relative",
      padding: theme.spacing(1, 3),
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1),
      },
      // backgroundColor: theme.palette.grey[200],
      backgroundColor: theme.palette.background.paper,
    },
    buttonGroupContainer: {
      display: "flex",
      flex: 1,
      alignItems: "center",
    },
    modeButton: {
      textTransform: "none",
    },
    modeButtonRoot: {
      padding: theme.spacing(0,3),
      height: "32px",
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        "&:hover": {
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
    content: {
      flex: 1,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    noEvalsMessage: {
      margin: theme.spacing(1, 3),
    },
    list: {
      marginBottom: theme.spacing(10),
      display: "flex",
      flexDirection: "column",
    },
    loading: {
      backgroundColor: "#fff",
    },
    pagination: {},
    searchFilterContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: theme.spacing(2),
      backgroundColor: '#fff',
      padding: theme.spacing(1, 2),
      borderRadius: theme.shape.borderRadius,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        '& > *:not(:last-child)': {
          marginBottom: theme.spacing(1)
        },
      }
    },
    search: {
      maxWidth: theme.breakpoints.width("sm"),
      width: "30%",
      backgroundColor: "#ffffff",
      borderColor: "#d0d0d0",
      borderWidth: "1px",
      borderStyle: "solid",
      [theme.breakpoints.down('sm')]: {
        minWidth: "97%"
      }
    },
    evalTypefilter: {
      maxWidth: theme.breakpoints.width("sm"),
      width: "20%",
      backgroundColor: "#ffffff",
      height: "42px",
      [theme.breakpoints.down('sm')]: {
        minWidth: "98%",
        padding: theme.spacing(0, 0)
      }
    },
    dateRangeFilter: {
      width: "100%",
      backgroundColor: "#ffffff",
      height: "35px",
      border: "1px solid #d0d0d0",
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0, 2)
    },
    dateRangeFilterContainer: {
      maxWidth: theme.breakpoints.width("sm"),
      width: "20%",
      [theme.breakpoints.down('sm')]: {
        minWidth: "95%",
        padding: theme.spacing(0, 0)
      }
    },
    clearFiltersButton: {
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
      }
    }
  })
);

const selectDraftMode = createSelector(
  (state: AppState) => state.evals.draftMode,
  draftMode => draftMode
);
const selectError = createSelector(
  (state: AppState) => state.evals.error,
  error => error
);
const selectLoadingEvals = createSelector(
  (state: AppState) => state.evals.loading,
  loading => loading
);
const selectEvalLimit = createSelector(
  (state: AppState) => state.evals.limit,
  limit => limit
);
const selectEvalOffset = createSelector(
  (state: AppState) => state.evals.offset,
  offset => offset
);
const selectEvalCount = createSelector(
  (state: AppState) => state.evals.total,
  count => count
);
const selectSearchText = createSelector(
  (state: AppState) => state.evals.searchText,
  searchText => searchText
);
const selectSelectedEvalType = createSelector(
  (state: AppState) => state.evals.selectedEvalType,
  selectedEvalType => selectedEvalType
);
const selectSelectedDateRange = createSelector(
  (state: AppState) => state.evals.selectedDateRange,
  selectedDateRange => selectedDateRange
);

interface EvalListProperties {}

export interface EvalListFilters {
  evalType: string;
  dateRange: Date[] | null;
}

const EvalList: FC<EvalListProperties> = props => {
  const classes = useStyles(props);
  const draftMode: boolean|null = useSelector(selectDraftMode);
  const evals = useSelector(selectEvals);
  const error = useSelector(selectError);
  const accountId = useSelector(selectUserId);
  const loadingEvals = useSelector(selectLoadingEvals);

  const totalEvals = useSelector(selectEvalCount);
  const limit = useSelector(selectEvalLimit);
  const offset = useSelector(selectEvalOffset);

  const searchText = useSelector(selectSearchText);
  const selectedEvalType = useSelector(selectSelectedEvalType);
  const selectedDateRange = useSelector(selectSelectedDateRange);

  const dispatch = useDispatch();
  const fetchingEvals = useSelector((state: AppState) => state.evals.isFetching);

  useEffect(() => {
    if (!fetchingEvals) {
      dispatch(loadEvaluations(draftMode ?? true, 25, undefined, undefined, undefined, searchText, {evalType: selectedEvalType, dateRange: selectedDateRange}));
    }
  }, [fetchingEvals, draftMode, dispatch])

  useEffect(
    function refresh() {
      dispatch(syncSystem());
    },
    [accountId, dispatch, draftMode]
  );

  if (error) {
    return (
      <div className={classes.root}>
        <ErrorBlock name={error.name} error={error} message={error.message} />
      </div>
    );
  }

  const handleListMode = (event: React.MouseEvent<HTMLElement>, newMode: boolean|null) => {
    if (newMode !== null) {
      dispatch(loadEvaluations(newMode, limit, undefined, undefined, undefined, searchText, {evalType: selectedEvalType, dateRange: selectedDateRange}));
    }
  };

  const handleSearch = (text: string) => {
    dispatch(loadEvaluations(draftMode, limit, 0, undefined, undefined, text, {evalType: selectedEvalType, dateRange: selectedDateRange}));
  };

  const handleEvalTypeChange = (value: string) => {
    dispatch(loadEvaluations(draftMode, limit, 0, undefined, undefined, searchText, {evalType: value, dateRange: selectedDateRange}));
  };

  const handleClearFilters = () => {
    dispatch(loadEvaluations(draftMode, limit, 0, undefined, undefined));
  };

  const handleDateRangeChange = (selectedDates: Date[]) => {
    if (selectedDates && selectedDates.length == 2) { 
      dispatch(loadEvaluations(draftMode, limit, 0, undefined, undefined, searchText, {evalType: selectedEvalType, dateRange: selectedDates}));
    }
  }

  const content =
    evals.length > 0 ? (
      <>
        {evals.map(function(evlId) {
          return <EvalListItem evalID={evlId} key={evlId} />;
        })}
      </>
    ) : (
      <Typography className={classes.noEvalsMessage}>
        {/* <ListItemText inset primary="No evaluations yet" /> */}
        No evaluations yet
      </Typography>
    );

  const toolbar = (
    <div className={classes.toolbar}>
      <div className={classes.buttonGroupContainer}>
        {/* <FilterList style={{marginRight: "4px"}} /> */}
        <ToggleButtonGroup
          aria-label="full-width contained primary button group"
          id="mode"
          value={draftMode}
          onChange={handleListMode}
          exclusive
        >
          <ToggleButton value={true} className={classes.modeButton} classes={{ root: classes.modeButtonRoot }}>
            Drafts
          </ToggleButton>
          <ToggleButton value={false} className={classes.modeButton} classes={{ root: classes.modeButtonRoot }}>
            Complete
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {draftMode && <NewEvalButton />}
    </div>
  );

  return (
    <div className={classes.root}>
      {toolbar}
      <Divider />

      <div style={{
        flex: 1,
        display: 'flex',
        overflowY: 'scroll',
      }}>

        <Container maxWidth="md" className={classes.content}>
          <div className={classes.searchFilterContainer}>
            <Search 
              className={classes.search} 
              searchText={searchText}
              onSearchUpdated={handleSearch} 
              placeholder="Search by facility name" 
              showClear />
            <Dropdown 
              className={classes.evalTypefilter} 
              label="Eval Type" 
              options={evalTypeOptions} 
              selectedValue={selectedEvalType}
              onDropdownOptionChange={handleEvalTypeChange} />
            <div className={classes.dateRangeFilterContainer}>
              <Flatpickr
                options={{
                  mode: 'range',
                  dateFormat: 'Y-m-d',
                }}
                value={selectedDateRange || undefined}
                onChange={handleDateRangeChange}
                placeholder="Date Range"
                className={classes.dateRangeFilter}
              />              
            </div>
            <Button
              className={classes.clearFiltersButton}
              disabled={!selectedEvalType && !selectedDateRange && !searchText}
              color="primary"
              variant="contained"
              onClick={handleClearFilters}>
                Clear Filters
            </Button>
          </div>
          <Paper elevation={1} className={classes.list}>
            <List>{(fetchingEvals || loadingEvals) ? <LoadingIndicator>Loading Evals</LoadingIndicator> : content}</List>
            <TablePagination
              className={classes.pagination}
              count={totalEvals}
              component="div"
              rowsPerPage={limit}
              page={offset === 0 ? 0 : offset / limit}
              onChangePage={(event, newPage) => dispatch(loadEvaluations(draftMode, limit, newPage * limit, undefined, undefined, searchText, {evalType: selectedEvalType, dateRange: selectedDateRange}))}
              onChangeRowsPerPage={event => dispatch(loadEvaluations(draftMode, parseInt(event!.target.value), 0, undefined, undefined, searchText, {evalType: selectedEvalType, dateRange: selectedDateRange}))}
            />
          </Paper>
        </Container>
      </div>
    </div>
  );
};

export default EvalList;